import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  MutationService,
  QueryClientService,
  QueryService,
} from '@ngneat/query';
import { Observable, tap } from 'rxjs';
import { CustomerDto, ThemeRepository } from 'src/app/_core';
import {
  INTERCEPT_ERRORS,
  INTERCEPT_NO_ERROR,
} from 'src/app/_core/interceptors/auth.interceptor';
import {
  ECommerceCustomer,
  EcommerceOutputLogin,
  InputChangePasswordDto,
  IStatus,
} from 'src/app/_core/types/types';
import { LoginDto } from 'src/app/routes/auth/forms/login.form';
import { AuthRepository } from 'src/app/routes/auth/state/auth.repository';
import { environment } from 'src/environments/environment';
import { CartFormService } from '../../public/modules/cart/forms/cart.form';
import { CartRepository } from '../../public/modules/cart/state/cart.repository';
import { CartService } from '../../public/modules/cart/state/cart.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _router = inject(Router);
  private readonly _httpClient = inject(HttpClient);
  private readonly _queryService = inject(QueryService);
  private readonly _mutationService = inject(MutationService);
  private readonly _authRepository = inject(AuthRepository);
  private readonly _themeRepository = inject(ThemeRepository);
  private readonly _cartFormService = inject(CartFormService);
  private readonly _queryClientService = inject(QueryClientService);
  private readonly _cartRepository = inject(CartRepository);
  private readonly _cartService = inject(CartService);

  createCustomer() {
    return this._mutationService.use((data: CustomerDto) => {
      return this._httpClient.post<IStatus>(
        `${environment.apiUrl}/e-commerce/customer`,
        { ...data, ecommerceId: environment.ecommerceInstanceId }
      );
    });
  }

  updateCustomer() {
    return this._mutationService.use((data: Partial<CustomerDto>) => {
      return this._httpClient.put<IStatus>(
        `${environment.apiUrl}/e-commerce/customer/me`,
        data
      );
    });
  }

  cancelEmailChange() {
    return this._mutationService.use(() => {
      return this._httpClient.delete<IStatus>(
        `${environment.apiUrl}/e-commerce/customer/me/pending-email`
      );
    });
  }

  removeAccount() {
    return this._mutationService.use((id: string) => {
      return this._httpClient.delete<IStatus>(
        `${environment.apiUrl}/e-commerce/customer/me`,
        { body: { id } }
      );
    });
  }

  login() {
    return this._mutationService.use((data: LoginDto) => {
      return this._httpClient
        .post<EcommerceOutputLogin>(
          `${environment.apiUrl}/e-commerce/auth/login`,
          {
            ...this.prepareLoginData(data),
            ecommerceInstanceId: environment.ecommerceInstanceId,
          }
        )
        .pipe(
          tap((res) => {
            this._authRepository.setToken(res.accessToken);
            this._authRepository.setRefreshToken(res.refreshToken);
          })
        );
    });
  }

  prepareLoginData(data: LoginDto) {
    return {
      ...data,
      email: data.email.toLowerCase(),
    };
  }

  googleLogin() {
    return this._mutationService.use((idToken: string) => {
      return this._httpClient
        .post<EcommerceOutputLogin>(
          `${environment.apiUrl}/e-commerce/auth/google`,
          {
            idToken,
            ecommerceId: environment.ecommerceInstanceId,
          }
        )
        .pipe(
          tap((res) => {
            this._authRepository.setToken(res.accessToken);
            this._authRepository.setRefreshToken(res.refreshToken);
          })
        );
    });
  }

  facebookLogin() {
    return this._mutationService.use((accessToken: string) => {
      return this._httpClient
        .post<EcommerceOutputLogin>(
          `${environment.apiUrl}/e-commerce/auth/facebook`,
          {
            accessToken,
            ecommerceId: environment.ecommerceInstanceId,
          }
        )
        .pipe(
          tap((res) => {
            this._authRepository.setToken(res.accessToken);
            this._authRepository.setRefreshToken(res.refreshToken);
          })
        );
    });
  }

  activateAccount() {
    return this._mutationService.use((token: string) => {
      return this._httpClient.post<EcommerceOutputLogin>(
        `${environment.apiUrl}/e-commerce/customer/activate`,
        { token },
        {
          context: new HttpContext().set(INTERCEPT_ERRORS, false),
        }
      );
    });
  }

  refreshToken(refreshToken: string): Observable<EcommerceOutputLogin> {
    return this._httpClient.post<EcommerceOutputLogin>(
      `${environment.apiUrl}/e-commerce/auth/refresh-token`,
      {
        refreshToken,
      }
    );
  }

  queryCurrentCustomer(noError = false) {
    console.log('noError', noError);
    return this._queryService.use(['current-customer'], () => {
      return this._httpClient.get<ECommerceCustomer>(
        `${environment.apiUrl}/e-commerce/customer/me`,
        {
          context: noError
            ? new HttpContext().set(INTERCEPT_NO_ERROR, true)
            : undefined,
        }
      );
    });
  }

  changePassword() {
    return this._mutationService.use((data: InputChangePasswordDto) => {
      return this._httpClient.patch<IStatus>(
        `${environment.apiUrl}/e-commerce/customer/change-password`,
        data
      );
    });
  }

  requestPasswordReset() {
    return this._mutationService.use((email: string) => {
      return this._httpClient.post<IStatus>(
        `${environment.apiUrl}/e-commerce/customer/request-password-reset`,
        { email }
      );
    });
  }

  resetPassword() {
    return this._mutationService.use(
      (data: { password: string; token: string }) => {
        return this._httpClient.post<IStatus>(
          `${environment.apiUrl}/e-commerce/customer/reset-password`,
          data
        );
      }
    );
  }

  logout(): void {
    this._authRepository.resetStore();

    this._cartFormService.resetInvoice();

    this._router.navigate(['/']);

    this._queryClientService.clear();

    this._cartRepository.resetCart();

    this._cartService.resetActiveCart();
  }
}
